import Head from 'next/head';

import ProjectPackage from '../../../../package.json';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import OneSignal from 'react-onesignal';
import { initOneSignal } from '../../../utils/onesignal';

interface IHeadContentProps {
  metaTitle: string;
  metaDescription: string;
  canonicalLink?: string;
  pageType?: string;
  slug?: string;
  keywords?: string;
  noindex?: boolean;
  flashTitle?: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  twitterCard?: 'summary' | 'summary_large_image';
}

const DEFAULT_OG_IMAGE = '/images/shared/og-image-default.jpg';

const HeadContent = ({
  metaTitle,
  metaDescription,
  canonicalLink = '/',
  pageType = 'pages:unknown',
  slug = '',
  keywords = '',
  noindex = false,
  flashTitle = '',
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  twitterCard = 'summary_large_image',
}: IHeadContentProps) => {
  const { asPath } = useRouter();
  const [pageTitle, setPageTitle] = useState(metaTitle);

  const isProduction =
    process.env.NEXT_PUBLIC_AIRBRAKE_ENVIRONMENT === 'production';
  const metaTagRobots = isProduction
    ? noindex
      ? 'noindex'
      : 'index, follow'
    : 'noindex, nofollow';

  const baseUrl = process.env.NEXT_PUBLIC_HOST;

  // Remove query strings, hash fragments, and trailing slashes from canonical URL
  const cleanPath =
    asPath?.split('?')[0].split('#')[0].replace(/\/+$/, '') || '';
  const canonicalUrl = `${baseUrl}${cleanPath || '/'}`;

  const ogImageUrl = ogImage?.startsWith('http')
    ? ogImage
    : `${baseUrl}${ogImage}`;

  function onFlashTitle(mainTitle: string, newTitle: string) {
    if (document.title == mainTitle) {
      setPageTitle(newTitle);
    } else {
      setPageTitle(mainTitle);
    }
  }

  useEffect(() => {
    const hasFlashTitle = !!flashTitle;

    if (hasFlashTitle) {
      const interval = setInterval(
        () => onFlashTitle(metaTitle, flashTitle),
        2000
      );

      return () => clearInterval(interval);
    }
  }, [flashTitle, metaTitle]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      initOneSignal({
        appId: 'c71bbe0c-2856-4425-a3c3-5fe0d6433cdf',
        notifyButton: {
          enable: true,
          prenotify: true,
          showCredit: false,
          text: {
            'dialog.blocked.message':
              'Siga estas instruções para permitir notificações.',
            'dialog.blocked.title': 'Desbloquear notificações',
            'dialog.main.button.subscribe': 'INSCREVER-SE',
            'dialog.main.button.unsubscribe': 'CANCELAR INSCRIÇÃO',
            'dialog.main.title': 'Receba notificações',
            'message.action.resubscribed':
              'Você está inscrito para receber notificações.',
            'message.action.subscribed': 'Obrigado por se inscrever!',
            'message.action.subscribing': 'Inscrevendo...',
            'message.action.unsubscribed':
              'Você não receberá mais notificações.',
            'message.prenotify':
              'Clique para se inscrever e receber notificações.',
            'tip.state.blocked': 'Você bloqueou as notificações.',
            'tip.state.subscribed':
              'Você está inscrito para receber notificações.',
            'tip.state.unsubscribed': 'Inscreva-se para receber notificações.',
          },
        },
      });
    }
  }, []);

  return (
    <Head>
      <meta name="gn-release" content={ProjectPackage.version} />

      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      <title>{pageTitle}</title>

      <meta name="description" content={metaDescription} />

      <meta name="keywords" content={keywords} />

      <meta name="robots" content={metaTagRobots} />

      <script
        id="gtm-script"
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','tagManagerDataLayer', 'GTM-T2HGD');`, // Esta variavel dataLayer foi alterado para tagManagerDataLayer no momento da criacao do site no script
        }}
      />

      <script
        id="tagmanager-configs"
        dangerouslySetInnerHTML={{
          __html: `(function(a,b){if('function'==typeof define&&define.amd)define(['exports'],b);else if('undefined'!=typeof exports)b(exports);else{var c={exports:{}};b(c.exports),a.tagmanagerWrap=c.exports}})(this,function(a){'use strict';function b(a,b,c){return b in a?Object.defineProperty(a,b,{value:c,enumerable:!0,configurable:!0,writable:!0}):a[b]=c,a}function c(a,b){if(!(a instanceof b))throw new TypeError('Cannot call a class as a function')}Object.defineProperty(a,'__esModule',{value:!0});var d=Object.assign||function(a){for(var b,c=1;c<arguments.length;c++)for(var d in b=arguments[c],b)Object.prototype.hasOwnProperty.call(b,d)&&(a[d]=b[d]);return a},e=function(){function a(a,b){for(var c,d=0;d<b.length;d++)c=b[d],c.enumerable=c.enumerable||!1,c.configurable=!0,'value'in c&&(c.writable=!0),Object.defineProperty(a,c.key,c)}return function(b,c,d){return c&&a(b.prototype,c),d&&a(b,d),b}}(),f={gtmId:'111111',virtualPageViewEvent:'virtual_pageview',startPush:{experiements:[]},addExperiments:[]},g=function(){function a(){var b=0<arguments.length&&void 0!==arguments[0]?arguments[0]:[],e=1<arguments.length&&void 0!==arguments[1]?arguments[1]:f;c(this,a),this.options=d({},f,e),this.dataLayer=b,this.dataLayer.push(this.options.startPush)}return e(a,[{key:'init',value:function(){d(window.tagManagerDataLayer,this.dataLayer),this.dataLayer=window.tagManagerDataLayer}},{key:'prependExperiment',value:function(a){var b=a.event,c=a.schema,d=a.data;this.options.startPush.experiments.push(d),this.custom({event:b,schema:c,data:d})}},{key:'virtualPageView',value:function(a,b){this.dataLayer.push({vpname:a,event:b||this.options.virtualPageViewEvent})}},{key:'eventCategory',value:function(a){var b=1<arguments.length&&void 0!==arguments[1]?arguments[1]:{},c=d({eventCategory:a,event:'GAEvent'},b);return this.dataLayer.push(c)}},{key:'custom',value:function(a){return this.dataLayer.push(a)}},{key:'bindEvents',value:function(){var a=this,b=[].slice.call(document.querySelectorAll('[data-gtm-event="ga-event"]'));return b.map(function(b){return b.getAttribute('data-gtm-bind')||(b.addEventListener('click',a._clickGAEvent.bind(a)),b.setAttribute('data-gtm-bind',!0)),b}),b}},{key:'_clickGAEvent',value:function(a){var b=a.currentTarget,c=this._getAttribute(b,'data-gtm-category'),d=this._getProps(b,['action','label','value','property']);this.eventCategory(c,d)}},{key:'_getProps',value:function(a,c){var e=this;return c.reduce(function(c,f){var g='event'+e._captalize(f),h=e._getAttribute(a,'data-gtm-'+f);return d({},c,b({},g,h))},{})}},{key:'_captalize',value:function(a){return a&&a[0].toUpperCase()+a.slice(1)}},{key:'_getAttribute',value:function(a,b){return a.getAttribute(b)||null}}]),a}();a.default=g});`,
        }}
      />

      <script
        id="tagmanager-options"
        dangerouslySetInnerHTML={{
          __html: `(function(){
              var tagManagerOptions = {
                page_type: '${pageType}',
                path_category: '${slug}',
              };
              var startPush = {
                page_type: tagManagerOptions.page_type,
                path_category: tagManagerOptions.path_category,
                experiments: window.experiments || []
              }
              window.tagManager = new window.tagmanagerWrap.default((window.tagManagerDataLayer || []), {
                gtmId: 'GTM-T2HGD',
                startPush
              });
              window.tagManager.init();
          })();`,
        }}
      />

      <script
        id="gtm-tech-script"
        dangerouslySetInnerHTML={{
          __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-M6Z3JBLN');`,
        }}
      />

      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-M6Z3JBLN"
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>

      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={ogType} />
      <meta property="og:image" content={ogImageUrl} />
      <meta property="og:site_name" content="GetNinjas" />
      <meta property="og:locale" content="pt_BR" />

      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={ogImageUrl} />

      <link rel="canonical" href={canonicalUrl} />
    </Head>
  );
};

export default HeadContent;
